export const DD_RUM_INITIALIZED_FLAG = '__DD_RUM_INITIALIZED__';
export const DD_LOGS_INITIALIZED_FLAG = '__DD_LOGS_INITIALIZED__';

export const DATADOG_EXPERIMENTAL_FEATURES = ['feature_flags', 'feature_operation_vital'] as const;

export const DEFAULT_ALLOWED_TRACING_URLS_PATTERN_SOURCE = String.raw`^https:\/\/(?:[a-z0-9-]+\.)*hilton\.com(?::\d+)?(?:[/?#]|$)`;

export type DatadogInlineInitConfig = {
  applicationId: string;
  clientToken: string;
  site?: string;
  service?: string;
  env?: string;
  version?: string;
  sessionSampleRate?: number;
  sessionReplaySampleRate?: number;
  trackUserInteractions?: boolean;
  trackResources?: boolean;
  trackLongTasks?: boolean;
  enableScaLogs?: boolean;
  allowedTracingUrlsPattern?: string;
  applicationName?: string;
  applicationUniqueId?: string;
};

function buildAllowedTracingUrlsExpression(allowedTracingUrlsPattern?: string): string {
  if (allowedTracingUrlsPattern) {
    return `new RegExp(${JSON.stringify(allowedTracingUrlsPattern)})`;
  }

  return `/${DEFAULT_ALLOWED_TRACING_URLS_PATTERN_SOURCE}/`;
}

function buildJsonInitFields(config: DatadogInlineInitConfig): string {
  const {
    applicationId,
    clientToken,
    site = 'datadoghq.com',
    service,
    env,
    version,
    sessionSampleRate = 100,
    sessionReplaySampleRate = 0,
    trackUserInteractions = true,
    trackResources = true,
    trackLongTasks = true,
  } = config;

  const fields: Record<string, unknown> = {
    applicationId,
    clientToken,
    site,
    sessionSampleRate,
    sessionReplaySampleRate,
    trackUserInteractions,
    trackResources,
    trackLongTasks,
    enablePrivacyForActionName: true,
    enableSessionReplay: false,
    enableExperimentalFeatures: [...DATADOG_EXPERIMENTAL_FEATURES],
    defaultPrivacyLevel: 'allow',
  };

  if (service) {
    fields['service'] = service;
  }
  if (env) {
    fields['env'] = env;
  }
  if (version) {
    fields['version'] = version;
  }

  return JSON.stringify(fields, null, 2)
    .slice(1, -1)
    .split('\n')
    .map((line) => `    ${line}`)
    .join('\n')
    .trimEnd();
}

function buildBeforeSendScript(): string {
  return `    beforeSend: function (event) {
      if (event.type === 'error' && event.error && event.error.message === 'Script error.') {
        return false;
      }
      if (event.type === 'resource' && event.resource.url.includes('graphql')) {
        var url = new URL(event.resource.url);
        var opName = url.searchParams.get('operationName');
        event.resource.url = service + '-' + opName;
      }
    },`;
}

function buildPostInitContextScript(
  applicationName?: string,
  applicationUniqueId?: string
): string {
  const lines = [
    '  var existingTabId = sessionStorage.getItem("custTabId");',
    '  if (!existingTabId) {',
    '    var newTabId = crypto.randomUUID();',
    '    sessionStorage.setItem("custTabId", newTabId);',
    '    window.DD_RUM.setGlobalContextProperty("custTabId", newTabId);',
    '  } else {',
    '    window.DD_RUM.setGlobalContextProperty("custTabId", existingTabId);',
    '  }',
    '',
    '  var locale = window.location.pathname.split("/")[1];',
    '  if (locale !== "en") {',
    '    locale = "non-en";',
    '  }',
    '  window.DD_RUM.setGlobalContextProperty("locale", locale);',
  ];

  if (applicationName) {
    lines.push(
      `  window.DD_RUM.setGlobalContextProperty("app-name", ${JSON.stringify(applicationName)});`
    );
  }

  if (applicationUniqueId) {
    lines.push(
      `  window.DD_RUM.setGlobalContextProperty("unique-id", ${JSON.stringify(applicationUniqueId)});`
    );
  }

  return lines.join('\n');
}

/**
 * Builds inline JS executed on DD_RUM.onReady to initialize RUM as early as possible.
 */
export function buildDatadogInitScript(config: DatadogInlineInitConfig): string {
  const serviceLiteral = JSON.stringify(config.service ?? '');
  const allowedTracingUrls = buildAllowedTracingUrlsExpression(config.allowedTracingUrlsPattern);
  const jsonFields = buildJsonInitFields(config);
  const beforeSend = buildBeforeSendScript();
  const postInitContext = buildPostInitContextScript(
    config.applicationName,
    config.applicationUniqueId
  );

  return `
window.DD_RUM.onReady(function () {
  if (window.${DD_RUM_INITIALIZED_FLAG}) {
    return;
  }
  window.${DD_RUM_INITIALIZED_FLAG} = true;

  var service = ${serviceLiteral};

  window.DD_RUM.init({
${jsonFields},
    allowedGraphQlUrls: [/https:\\/\\/.*\\.hilton\\.com\\/graphql/],
    allowedTracingUrls: [${allowedTracingUrls}],
${beforeSend}
  });

${postInitContext}
});`.trim();
}

/**
 * Builds inline JS executed on DD_LOGS.onReady for SCA GraphQL logging.
 */
export function buildDatadogScaLogsInitScript(config: DatadogInlineInitConfig): string {
  const { clientToken, site = 'datadoghq.com', service, env, version } = config;

  const fields: Record<string, unknown> = {
    clientToken,
    site,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
  };

  if (service) {
    fields['service'] = service;
  }
  if (env) {
    fields['env'] = env;
  }
  if (version) {
    fields['version'] = version;
  }

  return `
window.DD_LOGS.onReady(function () {
  if (window.${DD_LOGS_INITIALIZED_FLAG}) {
    return;
  }
  window.${DD_LOGS_INITIALIZED_FLAG} = true;

  window.DD_LOGS.init(${JSON.stringify(fields, null, 2)});
  window.DD_LOGS.createLogger('sca-graphql', { level: 'info' });
});`.trim();
}
